import React, { useState } from "react";
import { useEscape } from "@/hooks/useEscape";
import { LayoutGrid, Plus, Loader2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { TEMPLATES } from "@/components/boardsModel";

// The list of a workspace's boards, and the one place a board is created.
// Creation asks for a name and a template -- a board is never born empty.

function NewBoard({ workspaceId, onCreated, onClose }) {
  useEscape(true, onClose);
  const [name, setName] = useState("");
  const [template, setTemplate] = useState("projects");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/boards", { workspace_id: workspaceId, name: name.trim(), template });
      onCreated(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Nu s-a putut crea"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onMouseDown={onClose} data-testid="new-board-dialog">
      <form onSubmit={submit} onMouseDown={(e) => e.stopPropagation()} className="w-full max-w-md bg-surface border border-line/10 shadow-xl">
        <div className="px-4 py-3 border-b border-line/10 font-bold tracking-tight">Board nou</div>
        <div className="p-4 space-y-4">
          <label className="block space-y-1">
            <span className="label-tiny">Nume</span>
            <input autoFocus data-testid="new-board-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="ex. Roadmap Q4" className="input-flat text-sm py-1.5 w-full" />
          </label>
          <div className="space-y-1">
            <span className="label-tiny">Șablon</span>
            <div className="grid gap-1.5">
              {TEMPLATES.map((t) => (
                <label key={t.value} className={`flex items-start gap-2 p-2 border cursor-pointer ${template === t.value ? "border-brand bg-brand/5" : "border-line/10 hover:border-line/30"}`}>
                  <input type="radio" name="template" value={t.value} checked={template === t.value} onChange={() => setTemplate(t.value)} className="mt-0.5" data-testid={`template-${t.value}`} />
                  <span><span className="text-sm font-semibold block">{t.label}</span><span className="text-[11px] text-fg/50">{t.hint}</span></span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="btn-ghost text-sm px-3 py-1.5">Anulează</button>
            <button type="submit" data-testid="new-board-submit" disabled={busy || !name.trim()} className="btn-primary text-sm px-3 py-1.5 disabled:opacity-50 flex items-center gap-1.5">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Creează
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default function BoardsView({ workspaceId, boards, loading, onOpen, onCreated }) {
  const [creating, setCreating] = useState(false);
  return (
    <div className="h-full flex flex-col min-h-0" data-testid="boards-view">
      <div className="min-h-14 px-4 sm:px-6 py-2 flex items-center gap-3 border-b border-line/10 bg-bg">
        <LayoutGrid className="w-4 h-4 text-brand" />
        <h2 className="font-bold tracking-tight">Board-uri</h2>
        <span className="text-fg/40 text-xs">{boards.length}</span>
        <button type="button" data-testid="new-board" onClick={() => setCreating(true)} className="ml-auto btn-primary text-sm flex items-center gap-1.5 px-3 py-1.5">
          <Plus className="w-3.5 h-3.5" /> Board nou
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        {loading && boards.length === 0 ? (
          <p className="text-sm text-fg/40 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Se încarcă…</p>
        ) : boards.length === 0 ? (
          <div className="text-center py-20 px-6 max-w-md mx-auto">
            <LayoutGrid className="w-8 h-8 text-fg/20 mx-auto mb-3" />
            <p className="font-semibold">Niciun board încă</p>
            <p className="text-sm text-fg/50 mt-1 mb-4">Un board urmărește proiecte, sarcini sau orice listă cu status — coloane tipizate, grupuri, kanban.</p>
            <button type="button" onClick={() => setCreating(true)} className="btn-primary text-sm px-3 py-1.5">Creează primul board</button>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {boards.map((b) => (
              <div key={b.id} className="border border-line/10 bg-surface hover:border-line/30 transition-colors group/board relative">
                <button type="button" data-testid={`board-card-${b.id}`} onClick={() => onOpen(b.id)} className="w-full text-left p-4">
                  <p className="font-semibold truncate pr-6">{b.name}</p>
                  <p className="text-[11px] text-fg/50 mt-1">{b.item_count ?? 0} elemente · {b.columns?.length ?? 0} coloane · {b.groups?.length ?? 0} grupuri</p>
                  <div className="mt-3 flex flex-wrap gap-1">
                    {(b.columns || []).filter((c) => c.type === "status").slice(0, 1).flatMap((c) => c.options).map((o) => (
                      <span key={o.id} className="text-[10px] px-1.5 py-0.5 text-white" style={{ background: o.color }}>{o.label}</span>
                    ))}
                  </div>
                </button>
                <button type="button" onClick={() => window.open(`${window.location.origin}/?board=${b.id}&window=1`, "_blank", "noopener,width=1400,height=900")}
                  className="absolute top-3 right-3 p-1 text-fg/30 hover:text-brand opacity-0 group-hover/board:opacity-100" title="Deschide în fereastră separată" aria-label="Deschide în fereastră separată">
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      {creating && <NewBoard workspaceId={workspaceId} onClose={() => setCreating(false)} onCreated={(b) => { setCreating(false); onCreated(b); }} />}
    </div>
  );
}
